"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import { useQueryState } from "nuqs";
import { memo, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { sendContactMessage } from "@/app/(landing)/contact/actions";
import { contactFormSchema } from "@/app/(landing)/contact/types";
import {
  CONTACT_PAGE_MESSAGE_PARAM,
  CONTACT_PAGE_REFERRER_PARAM,
} from "@/app/(landing)/contact/utils";
import { Card, CardFooter } from "@/components/card";
import {
  FogBackground,
  type FogDensity,
} from "@/components/fog-background/fog-background";
import { Icon } from "@/components/icons";
import { ease } from "@/components/motion";
import { Text } from "@/components/text";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { INTERNAL_LINKS } from "@/lib/helpers/urls";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { AnimatePresence, LazyMotion, domAnimation, m } from "motion/react";
import Link from "next/link";
import Background from "./background.jpg";

const DebugToolbar = memo(
  ({
    currentState,
    setFormState,
  }: {
    currentState: FormState;
    setFormState: (state: FormState) => void;
  }) => {
    const [isDev, setIsDev] = useState(false);

    useEffect(() => {
      // Only show in development mode
      setIsDev(process.env.NODE_ENV === "development");
    }, []);

    if (!isDev) return null;

    return (
      <div className="fixed bottom-4 right-4 bg-neutral-800 p-3 rounded-lg shadow-lg z-50 opacity-90 hover:opacity-100 transition-opacity">
        <div className="text-xs font-semibold mb-2 text-neutral-50">
          Debug Toolbar - Current State: {currentState}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={currentState === "Form" ? "primary" : "outline"}
            onClick={() => setFormState("Form")}
            className="text-xs h-7 px-2"
          >
            Form
          </Button>
          <Button
            size="sm"
            variant={currentState === "FormSubmitting" ? "primary" : "outline"}
            onClick={() => setFormState("FormSubmitting")}
            className="text-xs h-7 px-2"
          >
            Submitting
          </Button>
          <Button
            size="sm"
            variant={currentState === "FormSuccess" ? "primary" : "outline"}
            onClick={() => setFormState("FormSuccess")}
            className="text-xs h-7 px-2"
          >
            Success
          </Button>
          <Button
            size="sm"
            variant={currentState === "FormFailed" ? "primary" : "outline"}
            onClick={() => setFormState("FormFailed")}
            className="text-xs h-7 px-2"
          >
            Failed
          </Button>
        </div>
      </div>
    );
  },
);
DebugToolbar.displayName = "DebugToolbar";

type ContactFormValues = z.infer<typeof contactFormSchema>;

type FormState = "Form" | "FormSubmitting" | "FormSuccess" | "FormFailed";

const SuccessState = memo(
  ({ onReset, message }: { onReset: () => void; message: string }) => {
    const { user } = useAuth();
    const isSignedIn = Boolean(user);

    return (
      <>
        <div className="space-y-3">
          <Text>We'll respond shortly.</Text>

          <div className="space-y-2">
            <Text>Your message:</Text>
            <div className="shadow-border rounded-lg px-4 py-3 bg-neutral-100">
              {message}
            </div>
          </div>
        </div>
        <CardFooter>
          <Button onClick={onReset} variant="outline" size="sm">
            Send Another Message
          </Button>

          <Button variant="primary" asChild size="sm">
            <Link
              href={
                isSignedIn ? INTERNAL_LINKS.DASHBOARD.HOME : INTERNAL_LINKS.HOME
              }
            >
              {isSignedIn ? "Go to Dashboard" : "Go to Home"}
            </Link>
          </Button>
        </CardFooter>
      </>
    );
  },
);
SuccessState.displayName = "SuccessState";

const ErrorState = memo(() => (
  <Alert variant="warning" icon="stroke">
    <Icon name="Info" className="translate-x-1" aria-hidden />
    <AlertTitle>Message not sent</AlertTitle>
    <AlertDescription>
      Your message wasn't received. Try sending again.
    </AlertDescription>
  </Alert>
));
ErrorState.displayName = "ErrorState";

const ContactFormContent = memo(
  ({
    form,
    onSubmit,
    isSubmitting,
    isError = false,
  }: {
    form: ReturnType<typeof useForm<ContactFormValues>>;
    onSubmit: (data: ContactFormValues) => void;
    isSubmitting: boolean;
    isError?: boolean;
  }) => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="homepage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Website</FormLabel>
              <FormControl>
                <Input autoComplete="url" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea
                  maxLength={10000}
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isError && <ErrorState />}

        <CardFooter>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={isSubmitting || form.formState.isSubmitting}
          >
            {isSubmitting
              ? "Sending..."
              : isError
                ? "Try again"
                : "Send Message"}
          </Button>
        </CardFooter>
      </form>
    </Form>
  ),
);
ContactFormContent.displayName = "ContactFormContent";

export default function Contact(props: {
  initialName?: string;
  initialEmail?: string;
}) {
  const [formState, setFormState] = useState<FormState>("Form");
  const [_, setErrorMessage] = useState<string>("");
  const [referrer] = useQueryState(CONTACT_PAGE_REFERRER_PARAM);
  const [initialMessage, setInitialMessage] = useQueryState(
    CONTACT_PAGE_MESSAGE_PARAM,
  );

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: props.initialName || "",
      email: props.initialEmail || "",
      homepage: "",
      message: initialMessage || "",
    },
    mode: "all",
  });

  const action = useAction(sendContactMessage, {
    onExecute: () => {
      setFormState("FormSubmitting");
    },
    onSuccess: (data) => {
      if (data.data?.success) {
        setFormState("FormSuccess");
        setInitialMessage(null);
        form.reset({
          name: "",
          email: "",
          homepage: "",
          message: "",
        });
      } else {
        setFormState("FormFailed");
        setErrorMessage(
          data.data?.error ?? "Something went wrong. Please try again.",
        );
      }
    },
    onError: (error) => {
      setFormState("FormFailed");
      if (typeof error === "string") {
        setErrorMessage(error);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
  });

  const onSubmit = useCallback(
    (data: ContactFormValues) => {
      // Reset error state if we're resubmitting after an error
      if (formState === "FormFailed") {
        setErrorMessage("");
      }
      action.execute({ ...data, referrer: referrer ?? undefined });
    },
    [action, formState, referrer],
  );

  const resetForm = useCallback(() => {
    setFormState("Form");
    setErrorMessage("");
    form.clearErrors();
  }, [form]);

  let title = "Contact us";
  switch (formState) {
    case "FormSuccess":
      title = "Your message is received";
      break;
  }
  // Render content based on form state
  const renderContent = () => {
    switch (formState) {
      case "FormFailed":
        return (
          <>
            <ContactFormContent
              form={form}
              onSubmit={onSubmit}
              isSubmitting={false}
              isError={true}
            />
          </>
        );
      default:
        return (
          <ContactFormContent
            form={form}
            onSubmit={onSubmit}
            isSubmitting={formState === "FormSubmitting"}
          />
        );
    }
  };

  let density: FogDensity = "medium";

  switch (formState) {
    case "FormSuccess":
    case "FormFailed":
      density = "clear";
      break;
  }

  return (
    <div className="h-full">
      <FogBackground density={density} image={Background} />
      <LazyMotion features={domAnimation}>
        <div className="w-full max-w-xl mx-auto relative">
          <m.div
            animate={
              formState === "FormSuccess"
                ? {
                    filter: "blur(1.5px)",
                    scale: 0.95,
                    opacity: 0,
                  }
                : {
                    filter: "blur(0px)",
                    scale: 1,
                    opacity: 1,
                  }
            }
            style={{
              transformOrigin: "top center",
            }}
            transition={ease.spring.elegant}
          >
            <Card
              title="Contact us"
              className="w-full max-w-[revert]"
              titleClassName="text-base"
            >
              {renderContent()}
            </Card>
          </m.div>

          <AnimatePresence>
            {formState === "FormSuccess" && (
              <m.div
                className="absolute inset-0 top-0"
                initial={{
                  opacity: 0,
                  y: 100,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 100,
                }}
                transition={ease.spring.elegant}
              >
                <Card
                  title="Your message is received"
                  className="w-full max-w-[revert]"
                >
                  <SuccessState
                    onReset={resetForm}
                    message={action.input?.message || ""}
                  />
                </Card>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </LazyMotion>
      <DebugToolbar currentState={formState} setFormState={setFormState} />
    </div>
  );
}
